import { registerBidder } from 'prebid.js/src/adapters/bidderFactory'
import {
  formatQueryString,
  getQueryParam,
  parseQueryString
} from '../../helpers/getQueryParam'

const AD_TAG = 'https://a.b'
const BIDDER_CODE = 'ixVideo'
const ENDPOINT = 'https://as-sec.casalemedia.com/playlist'
const TEST_ENDPOINT = 'https://ssp-sandbox.casalemedia.com/playlist'
const CREATIVE_ENDPOINT = 'https://as-sec.casalemedia.com/pcreative'
const TEST_CREATIVE_ENDPOINT = 'https://ssp-sandbox.casalemedia.com/pcreative'
export const REV_SHARE = 0.9
const parser = new DOMParser()
let isTest = false

export const spec = {
  aliases: ['indexExchangeVideo'],
  code: BIDDER_CODE,
  supportedMediaTypes: ['video'],

  buildRequests(validBidRequests, bidderRequest) {
    const bid = bidderRequest.bids[0]

    let gdprApplies =
      bidderRequest.gdprConsent && bidderRequest.gdprConsent.gdprApplies
    if (gdprApplies === true) {
      gdprApplies = {
        ix_c: bidderRequest.gdprConsent.consentString,
        ix_g: '1'
      }
    } else if (gdprApplies === false) {
      gdprApplies = {
        ix_c: bidderRequest.gdprConsent.consentString,
        ix_g: '0'
      }
    } else {
      // We don't know, in this case. The video_player probably loaded before the web-wrapper.
      // IX wants ix_g and ix_c to be omitted in this case.
      gdprApplies = {}
    }

    // https://kb.indexexchange.com/Video/Technical_Reference.htm
    const data = {
      ...gdprApplies,
      ix_api: bid.params.api.join('~'),
      ix_id: bid.bidId,
      ix_js: 1,
      ix_s: bid.params.siteId,
      ix_sz: `${bid.params.width}x${bid.params.height}`,
      ix_to: bid.params.timeout,
      ix_u: bid.params.referrer,
      ix_v: '10.0',
      ix_vami: bid.params.mimes.join('~'),
      ix_vasd: bid.params.delay,
      ix_vastp: bid.params.protocols.join('~'),
      ix_vt: AD_TAG
    }
    isTest = bid.params.debug

    return {
      data,
      method: 'GET',
      url: bid.params.debug ? TEST_ENDPOINT : ENDPOINT
    }
  },

  getUserSyncs(syncOptions, serverResponses) {
    return []
  },

  interpretResponse(serverResponse, request) {
    const xml = parser.parseFromString(serverResponse.body, 'text/xml')
    const error = !!xml.getElementsByTagName('Error').length
    const vastAdTagURI = xml.getElementsByTagName('VASTAdTagURI')[0]
      .textContent as string
    const queryString = vastAdTagURI.replace(`${AD_TAG}`, '')
    const custParams = getQueryParam('cust_params', queryString) as string

    if (!custParams) {
      return []
    }

    const paramsString = decodeURIComponent(custParams)
    const data = parseQueryString(paramsString)
    const size = request.data.ix_sz.split('x').map((v) => parseInt(v, 10))
    const bidResponse = {
      ad: '',
      cpm: parseCpm(data.IVT, data.pr_1_1_s_cp as string),
      creativeId: data.pr_1_1_s_pcid,
      currency: 'USD',
      hb_vpmp_ixVideo: isPMP(data.IVT) ? 1 : 0,
      height: size[1],
      mediaType: 'video',
      netRevenue: true,
      requestId: request.data.ix_id,
      ttl: 300,
      vastUrl: createVastUrl(data),
      width: size[0]
    }

    return [bidResponse]
  },

  isBidRequestValid(bidRequest) {
    return !!(
      bidRequest &&
      bidRequest.bidder === BIDDER_CODE &&
      bidRequest.params &&
      Array.isArray(bidRequest.params.api) &&
      typeof bidRequest.params.bidFloor === 'number' &&
      typeof bidRequest.params.debug === 'boolean' &&
      Array.isArray(bidRequest.params.deliveryMethods) &&
      Array.isArray(bidRequest.params.mimes) &&
      Array.isArray(bidRequest.params.protocols) &&
      typeof bidRequest.params.referrer === 'string' &&
      typeof bidRequest.params.secure === 'boolean' &&
      typeof bidRequest.params.siteId === 'number' &&
      typeof bidRequest.params.height === 'number' &&
      typeof bidRequest.params.width === 'number' &&
      typeof bidRequest.params.timeout === 'number'
    )
  },

  onBidWon(bid) {
    //
  },

  onSetTargeting(bid) {
    //
  },

  onTimeout(timeoutData) {
    //
  }
}

registerBidder(spec)

export default spec

export function parseCpm(ivt: string = '', closingPrice: string): number {
  if (!ivt) {
    return 0.0
  }
  const ivtValue = ivt.split('_').pop() as string
  if (isPMP(ivt)) {
    return closingPrice ? (parseFloat(closingPrice) || 0) * REV_SHARE : 0.0
  } else {
    return (parseInt(ivtValue, 10) || 0) / 100
  }
}

function isPMP(ivt: string): boolean {
  const ivtValue = ivt.split('_').pop()
  const maxIVTValueLength = 6
  // If IVTValue is 6 or more digits, that means it's actually the "Deal ID", and PMP.
  return !!ivtValue && ivtValue.length >= maxIVTValueLength
}

export function createVastUrl(data: any): string {
  const endpoint = isTest ? TEST_CREATIVE_ENDPOINT : CREATIVE_ENDPOINT
  const params = {
    aid: data.pr_1_1_s_aid || '',
    au: data.pc_au || '',
    c: data.pr_1_1_s_c || '',
    cp: data.pr_1_1_s_cp || '',
    d: data.pr_1_1_s_d || '',
    dm: data.pr_1_1_s_dm || '',
    epr: data.pc_epr || '',
    fb: data.pr_1_1_s_fb || '',
    m: data.pr_1_1_s_m || '',
    n: data.pc_n || '',
    npm: data.pr_1_1_s_npm || '',
    pcid: data.pr_1_1_s_pcid || '',
    pr: data.pc_pr || '',
    r: data.pr_1_1_s_r || '',
    s: data.pr_1_1_s_s || '',
    sid: data.pr_1_1_s_sid || '',
    t: data.pr_1_1_s_t || '',
    tid: data.pr_1_1_s_tid || '',
    u: data.pc_u || '',
    unk1: data.pc_unk1 || '',
    unk2: data.pr_1_1_s_unk2 || '',
    wp: data.pr_1_1_s_wp || ''
  }
  return `${endpoint}?${formatQueryString(params)}`
}
